import { Fragment } from "react"
import { Box, Tooltip, Typography } from "@mui/material"

export type SplitBarRow = {
  id: string
  name: string
  // The configured goal share; null when the rule has no goal (the rotation).
  targetPct: number | null
  // The share the consultant actually got, or would get in a preview.
  actualPct: number
  barTooltip?: string
  deltaTooltip?: string
}

// The shared split visualization: one row per consultant, a fill bar coloured by
// how far off target they land, a black target marker, the "goal% → got%" text,
// and the drift. Used by the rule editor preview and the main-page rule split.
export function SplitBars(props: { rows: SplitBarRow[] }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(130px, 1.1fr) 2fr max-content 42px",
        columnGap: 1.5,
        rowGap: 0.75,
        alignItems: "center",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {props.rows.map((row) => {
        const hasTarget = row.targetPct !== null
        const target = row.targetPct ?? 0
        const drift = Math.round((row.actualPct - target) * 10) / 10
        const starved = row.actualPct === 0 && hasTarget && target >= 1
        const onTarget = !hasTarget || (!starved && Math.abs(drift) <= 5)
        const statusColor = onTarget ? "success.main" : "warning.dark"
        return (
          <Fragment key={row.id}>
            <Typography variant="body2" noWrap>
              {row.name}
            </Typography>
            <Box
              sx={{
                position: "relative",
                height: 16,
                bgcolor: "action.hover",
                borderRadius: 0.75,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${Math.min(row.actualPct, 100)}%`,
                  bgcolor: statusColor,
                  borderRadius: 0.75,
                }}
              />
              {hasTarget && (
                <Box
                  sx={{
                    position: "absolute",
                    left: `${Math.min(target, 100)}%`,
                    top: -1,
                    bottom: -1,
                    width: 2,
                    bgcolor: "common.black",
                  }}
                />
              )}
            </Box>
            <Tooltip
              title={row.barTooltip ?? ""}
              disableHoverListener={!row.barTooltip}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  whiteSpace: "nowrap",
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  cursor: row.barTooltip ? "help" : "default",
                }}
              >
                {hasTarget ? (
                  <>
                    {target}% →{" "}
                    <Box
                      component="span"
                      sx={{ color: "text.primary", fontWeight: 400 }}
                    >
                      {row.actualPct}%
                    </Box>
                  </>
                ) : (
                  <Box component="span" sx={{ color: "text.primary" }}>
                    {row.actualPct}%
                  </Box>
                )}
              </Typography>
            </Tooltip>
            {hasTarget ? (
              <Tooltip
                title={row.deltaTooltip ?? ""}
                disableHoverListener={!row.deltaTooltip}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: statusColor,
                    fontWeight: 400,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    cursor: row.deltaTooltip ? "help" : "default",
                  }}
                >
                  {drift > 0 ? `+${drift}` : drift}
                </Typography>
              </Tooltip>
            ) : (
              <span />
            )}
          </Fragment>
        )
      })}
    </Box>
  )
}
